import React, { useState } from 'react';
import { Button } from '@roger/ui-components';
import { Loader2, RefreshCw } from 'lucide-react';
import { useStore } from '../../../store';
import {
  refreshExternalEmrCensus,
  type ExternalEmrSummary,
} from '../../../services/externalEmrPatients';
import { getBusinessErrorMessage } from '../../../utils/apiErrorDetail';
import { formatDateTimeInAgencyZone } from '../../../utils/dateUtils';
import {
  CENSUS_ALREADY_RUNNING,
  REFRESHING_CENSUS_HELP,
  REFRESHING_CENSUS_LABEL,
  REFRESH_CENSUS_ERROR,
  REFRESH_CENSUS_LABEL,
  SUMMARY_LOAD_ERROR,
  externalEmrSummaryLine,
  lastCensusLine,
  takeOverPendingLine,
} from './externalEmrPatientCopy';

interface ExternalEmrSummaryHeaderProps {
  summary: ExternalEmrSummary | null;
  failed: boolean;
  /**
   * Render gate for Refresh census (`chart-import:start`). When the summary
   * says the agency cannot import now, its reason shows in the button's place.
   */
  canRefresh: boolean;
  /**
   * A census read started (or was already running): wait for its finish.
   * A short read can finish before the counts ever show it running.
   */
  onStarted: () => void;
  /** A pressed refresh has not finished yet. */
  awaiting?: boolean;
  /**
   * Take Over v2 is available to the viewer. Without it there is no Take
   * Over to press, so the header never says charts need one.
   */
  canTakeOver?: boolean;
  /** Open the charts the Take Over line counts (the Needs Take Over chip). */
  onShowTakeOver?: () => void;
}

/** Company-wide counts for External EMR patients, and the census refresh. */
export const ExternalEmrSummaryHeader: React.FC<
  ExternalEmrSummaryHeaderProps
> = ({
  summary,
  failed,
  canRefresh,
  onStarted,
  awaiting = false,
  canTakeOver = false,
  onShowTakeOver,
}) => {
  const showError = useStore(state => state.showError);
  const showSuccess = useStore(state => state.showSuccess);
  const [posting, setPosting] = useState(false);
  // Busy while the start request is out, until a pressed read finishes, and
  // for as long as the server says a read is running (also mid-run opens).
  const refreshing =
    posting || awaiting || summary?.census_sync?.status === 'running';
  const blockedReason =
    summary?.import_ready === false
      ? (summary.import_blocked_reason ?? null)
      : null;
  // Readiness rides on the summary. Until it answers, show neither Refresh nor
  // the reason, so Refresh never flashes before a reason replaces it.
  const readinessPending = !summary && !failed;

  const refresh = async () => {
    setPosting(true);
    try {
      const result = await refreshExternalEmrCensus();
      if (result?.status === 'already_running') {
        showSuccess(CENSUS_ALREADY_RUNNING);
      }
      onStarted();
    } catch (error) {
      console.error('Failed to start the External EMR census read:', error);
      showError(getBusinessErrorMessage(error) ?? REFRESH_CENSUS_ERROR);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div
      className='flex flex-col gap-2 rounded-md border bg-white p-3 sm:flex-row sm:items-center sm:justify-between'
      data-testid='external-emr-summary'
    >
      <div className='min-w-0 space-y-0.5'>
        {summary ? (
          <>
            <p
              className='text-sm font-medium'
              data-testid='external-emr-counts'
            >
              {externalEmrSummaryLine(summary)}
            </p>
            {canTakeOver &&
              summary.last_day &&
              (summary.take_over_pending ?? 0) > 0 && (
                <Button
                  variant='link'
                  size='sm'
                  onClick={onShowTakeOver}
                  data-testid='take-over-pending'
                >
                  {takeOverPendingLine(
                    summary.take_over_pending ?? 0,
                    summary.last_day
                  )}
                </Button>
              )}
            <p className='text-xs text-muted-foreground'>
              {lastCensusLine(
                summary.last_census_at
                  ? formatDateTimeInAgencyZone(summary.last_census_at)
                  : ''
              )}
            </p>
          </>
        ) : (
          <p className='text-sm text-muted-foreground'>
            {failed ? SUMMARY_LOAD_ERROR : '…'}
          </p>
        )}
        {refreshing && (
          <p
            className='text-xs text-muted-foreground'
            data-testid='refresh-census-help'
          >
            {REFRESHING_CENSUS_HELP}
          </p>
        )}
      </div>
      {canRefresh && blockedReason && (
        <p
          className='text-sm text-gray-700 sm:max-w-sm sm:text-right'
          data-testid='import-blocked-reason'
        >
          {blockedReason}
        </p>
      )}
      {canRefresh && !blockedReason && !readinessPending && (
        <Button
          size='sm'
          variant='outline'
          className='w-full sm:w-auto'
          disabled={refreshing}
          onClick={() => void refresh()}
          data-testid='refresh-census'
        >
          {refreshing ? (
            <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
          ) : (
            <RefreshCw className='mr-1.5 h-4 w-4' />
          )}
          {refreshing ? REFRESHING_CENSUS_LABEL : REFRESH_CENSUS_LABEL}
        </Button>
      )}
    </div>
  );
};
